import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NumberField } from "@/components/NumberField";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Wand2, Plus, Trash2, Clock, Sparkles, Scissors, HeartHandshake, Camera,
  Settings2, AlertTriangle, Share2, Download, Link as LinkIcon, Copy,
  Smartphone, Mail, ChevronDown, User as UserIcon, X,
} from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { TimePicker } from "@/components/TimePicker";

// -------------------------------------------------------------
// Helpers
// -------------------------------------------------------------
function addMinutes(hhmm, minutes) {
  if (!hhmm || !hhmm.includes(":")) return "";
  const [h, m] = hhmm.split(":").map((x) => parseInt(x, 10));
  if (Number.isNaN(h) || Number.isNaN(m)) return "";
  const dur = parseInt(minutes, 10) || 0;
  const total = ((h * 60 + m + dur) % (24 * 60) + 24 * 60) % (24 * 60);
  const H = Math.floor(total / 60);
  const M = total % 60;
  return `${String(H).padStart(2, "0")}:${String(M).padStart(2, "0")}`;
}

function toMin(t) {
  if (!t || !t.includes(":")) return null;
  const [h, m] = t.split(":");
  const H = parseInt(h, 10);
  const M = parseInt(m, 10);
  if (Number.isNaN(H) || Number.isNaN(M)) return null;
  return H * 60 + M;
}

const TYPE_META = {
  setup:    { label: "Setup",       icon: Settings2,     tint: "bg-neutral-100 text-neutral-700 border-neutral-200" },
  hair:     { label: "Hair",        icon: Scissors,      tint: "bg-amber-50 text-amber-800 border-amber-200" },
  makeup:   { label: "Makeup",      icon: Sparkles,      tint: "bg-rose-50 text-rose-800 border-rose-200" },
  combo:    { label: "Entrambi",    icon: Sparkles,      tint: "bg-primary/10 text-primary border-primary/20" },
  ready:    { label: "Pronte",      icon: Camera,        tint: "bg-neutral-100 text-neutral-700 border-neutral-200" },
  ceremony: { label: "Cerimonia",   icon: HeartHandshake, tint: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  travel:   { label: "Spostamento", icon: Clock,         tint: "bg-neutral-100 text-neutral-600 border-neutral-200" },
  other:    { label: "Altro",       icon: Clock,         tint: "bg-neutral-100 text-neutral-600 border-neutral-200" },
};
const PRIMARY_TYPES = ["hair", "makeup", "combo"];
const SECONDARY_TYPES = ["setup", "travel", "ready", "ceremony", "other"];

const newBlockId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : String(Math.random()).slice(2);

// Detects overlapping operator assignments across blocks (same shape as before).
function detectOverlaps(blocks) {
  const conflicts = new Map();
  const ensure = (id) => {
    if (!conflicts.has(id)) conflicts.set(id, { lead: [], assistant: [] });
    return conflicts.get(id);
  };
  const byOperator = { lead: new Map(), assistant: new Map() };
  blocks.forEach((b) => {
    const start = toMin(b.time);
    const dur = parseInt(b.duration, 10) || 0;
    if (start == null || dur <= 0) return;
    const end = start + dur;
    [["lead", b.lead_id], ["assistant", b.assistant_id]].forEach(([role, opId]) => {
      if (!opId) return;
      const list = byOperator[role].get(opId) || [];
      list.push({ id: b.id, start, end });
      byOperator[role].set(opId, list);
    });
  });
  Object.entries(byOperator).forEach(([role, map]) => {
    map.forEach((list) => {
      for (let i = 0; i < list.length; i++) {
        for (let j = i + 1; j < list.length; j++) {
          const a = list[i], c = list[j];
          if (a.start < c.end && c.start < a.end) {
            ensure(a.id)[role].push(c.id);
            ensure(c.id)[role].push(a.id);
          }
        }
      }
    });
  });
  return conflicts;
}

const EMPTY_DRAFT = {
  id: "",
  time: "",
  duration: 60,
  title: "",
  type: "hair",
  lead_id: "",
  assistant_id: "",
  notes: "",
};

// -------------------------------------------------------------
// Compact card (read-only)
// -------------------------------------------------------------
function TimelineCard({ block, team, hasConflict, onOpen, testid }) {
  const meta = TYPE_META[block.type] || TYPE_META.other;
  const start = block.time || "—";
  const end = block.time && (parseInt(block.duration, 10) || 0) > 0
    ? addMinutes(block.time, block.duration)
    : "";
  const leadName = team.find((t) => t.id === block.lead_id)?.name;
  const asstName = team.find((t) => t.id === block.assistant_id)?.name;
  const operatorLine = [leadName, asstName].filter(Boolean).join(" · ");

  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={testid}
      className={[
        "w-full text-left bg-white border rounded-sm p-3 sm:p-4",
        "flex items-stretch gap-3 transition",
        "active:scale-[0.995] active:bg-neutral-50",
        "hover:border-primary/40 hover:shadow-sm",
        hasConflict ? "border-red-300 ring-1 ring-red-200" : "border-border",
      ].join(" ")}
    >
      {/* Time column */}
      <div className="flex flex-col items-start justify-center min-w-[54px] shrink-0 pr-3 border-r border-border/70 tabular-nums">
        <span className="text-sm font-medium text-neutral-900 leading-tight">{start}</span>
        {end && (
          <span className="text-xs text-neutral-500 leading-tight mt-0.5">{end}</span>
        )}
      </div>

      {/* Main content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-neutral-900 truncate">
              {block.title || <span className="text-neutral-400">Senza titolo</span>}
            </div>
            <div className="mt-0.5 text-xs text-neutral-600 flex items-center gap-1 truncate">
              <meta.icon className="w-3 h-3 shrink-0" strokeWidth={1.75} />
              <span className="truncate">
                {meta.label}
                {operatorLine ? <> · <span className="text-neutral-500">{operatorLine}</span></> : null}
              </span>
            </div>
          </div>
          <span
            className={`shrink-0 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${meta.tint}`}
          >
            {meta.label}
          </span>
        </div>

        {hasConflict && (
          <div className="mt-2 flex items-center gap-1.5 text-[11px] text-red-700">
            <AlertTriangle className="w-3 h-3 shrink-0" strokeWidth={1.75} />
            <span>Sovrapposizione operatore</span>
          </div>
        )}
      </div>
    </button>
  );
}

// -------------------------------------------------------------
// Draft editor (Sheet bottom on mobile / centered on desktop)
// -------------------------------------------------------------
// Detect a "touch-first" pointer once at module load. `pointer: coarse`
// is the CSS media feature that reliably identifies phones + tablets
// across iOS/Android; a desktop with a touchscreen but a mouse keeps
// `pointer: fine`, so it correctly falls back to the custom picker.
const IS_TOUCH_DEVICE =
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(pointer: coarse)").matches;

// Blur the currently focused text input/textarea (if any). Used before
// opening non-text controls (pills, selects, time picker, footer buttons)
// so the iOS keyboard is dismissed automatically — no manual "Fine" tap.
function blurActiveTextInput() {
  if (typeof document === "undefined") return;
  const el = document.activeElement;
  if (!el || typeof el.blur !== "function") return;
  const tag = el.tagName;
  const isText =
    tag === "INPUT" || tag === "TEXTAREA" || el.getAttribute?.("contenteditable") === "true";
  if (isText) el.blur();
}

// When a text field receives focus on mobile, scroll it to the middle of
// the sheet so the keyboard doesn't cover it. The 300ms delay gives iOS
// time to animate the keyboard in — running earlier means the browser's
// own auto-scroll uses the pre-keyboard viewport and we end up short.
function scrollFocusedIntoView(e) {
  if (!IS_TOUCH_DEVICE) return;
  const target = e?.target;
  if (!target || typeof target.scrollIntoView !== "function") return;
  setTimeout(() => {
    try { target.scrollIntoView({ block: "center", behavior: "smooth" }); }
    catch { /* no-op */ }
  }, 300);
}

function BlockEditor({
  open, mode, draft, setDraft, team, onCancel, onSubmit, onDelete,
  defaultStartTime,
}) {
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    if (open) {
      setShowAdvanced(!!draft && !PRIMARY_TYPES.includes(draft.type));
    }
  }, [open, draft]);

  if (!draft) return null;

  const update = (patch) => setDraft((d) => ({ ...d, ...patch }));

  const commit = (e) => {
    e?.preventDefault?.();
    // Ensure the iOS keyboard is dismissed before we run validation +
    // dispatch — otherwise the toast can be hidden behind it.
    blurActiveTextInput();
    const title = (draft.title || "").trim();
    if (!title) {
      toast.error("Aggiungi un nome per l'ospite / attività");
      return;
    }
    const duration = parseInt(draft.duration, 10) || 0;
    onSubmit({
      ...draft,
      title,
      duration,
      time: draft.time || "",
    });
  };

  const requestCancel = () => { blurActiveTextInput(); onCancel(); };
  const requestDelete = () => { blurActiveTextInput(); onDelete?.(); };

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) requestCancel(); }}>
      <SheetContent
        side="bottom"
        // Full-height flex column: header and footer never scroll, only
        // the middle <form> does. Using `dvh` (dynamic viewport height)
        // instead of `vh` keeps the sheet correctly sized when the iOS
        // keyboard opens — `vh` freezes at the pre-keyboard viewport.
        className="rounded-t-xl border-t p-0 flex flex-col h-[92dvh] max-h-[92dvh] sm:max-w-lg sm:mx-auto sm:rounded-xl sm:inset-x-0 sm:my-auto sm:h-auto sm:max-h-[86dvh]"
        data-testid="timeline-editor-sheet"
      >
        <SheetHeader className="shrink-0 px-5 pt-5 pb-3 border-b border-border bg-white">
          <SheetTitle className="font-serif-heading text-xl text-left">
            {mode === "new" ? "Nuovo ospite / attività" : "Modifica attività"}
          </SheetTitle>
          <SheetDescription className="sr-only">
            Compila i campi e conferma per aggiornare la timeline.
          </SheetDescription>
        </SheetHeader>

        <form
          onSubmit={commit}
          className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-5 py-4 space-y-4"
        >
          {/* Name */}
          <div>
            <Label className="label-uppercase text-[11px]">Nome ospite / attività</Label>
            <Input
              autoFocus={mode === "new"}
              value={draft.title}
              onChange={(e) => update({ title: e.target.value })}
              onFocus={scrollFocusedIntoView}
              placeholder="Es. Mamma sposa"
              className="mt-1 rounded-sm h-11"
              data-testid="timeline-editor-name"
            />
          </div>

          {/* Service pills */}
          <div>
            <Label className="label-uppercase text-[11px]">Servizio</Label>
            <div className="mt-1 grid grid-cols-3 gap-2">
              {PRIMARY_TYPES.map((t) => {
                const meta = TYPE_META[t];
                const active = draft.type === t;
                return (
                  <button
                    key={t}
                    type="button"
                    onPointerDown={blurActiveTextInput}
                    onClick={() => update({ type: t })}
                    className={[
                      "h-11 rounded-sm border text-sm inline-flex items-center justify-center gap-1.5 transition",
                      active
                        ? "bg-primary text-primary-foreground border-primary shadow-sm"
                        : "bg-white text-neutral-700 border-border hover:border-primary/40",
                    ].join(" ")}
                    data-testid={`timeline-editor-type-${t}`}
                  >
                    <meta.icon className="w-4 h-4" strokeWidth={1.75} />
                    {meta.label}
                  </button>
                );
              })}
            </div>

            <button
              type="button"
              onPointerDown={blurActiveTextInput}
              onClick={() => setShowAdvanced((v) => !v)}
              className="mt-2 inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-primary"
              data-testid="timeline-editor-advanced-toggle"
            >
              <ChevronDown className={`w-3 h-3 transition ${showAdvanced ? "rotate-180" : ""}`} strokeWidth={1.75} />
              Altro tipo…
            </button>
            {showAdvanced && (
              <Select
                value={SECONDARY_TYPES.includes(draft.type) ? draft.type : ""}
                onValueChange={(v) => update({ type: v })}
              >
                <SelectTrigger
                  className="mt-2 rounded-sm h-11"
                  onPointerDown={blurActiveTextInput}
                  data-testid="timeline-editor-secondary-type"
                >
                  <SelectValue placeholder="Categoria (Cerimonia, Setup, ecc.)" />
                </SelectTrigger>
                <SelectContent>
                  {SECONDARY_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>{TYPE_META[t].label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Operator */}
          <div>
            <Label className="label-uppercase text-[11px]">Operatore</Label>
            <Select
              value={draft.lead_id || "none"}
              onValueChange={(v) => update({ lead_id: v === "none" ? "" : v })}
            >
              <SelectTrigger
                className="mt-1 rounded-sm h-11"
                onPointerDown={blurActiveTextInput}
                data-testid="timeline-editor-lead"
              >
                <SelectValue placeholder="Seleziona operatore" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Nessuno —</SelectItem>
                {team.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name} · {t.role}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Assistant */}
          <div>
            <Label className="label-uppercase text-[11px]">Assistente</Label>
            <Select
              value={draft.assistant_id || "none"}
              onValueChange={(v) => update({ assistant_id: v === "none" ? "" : v })}
            >
              <SelectTrigger
                className="mt-1 rounded-sm h-11"
                onPointerDown={blurActiveTextInput}
                data-testid="timeline-editor-assistant"
              >
                <SelectValue placeholder="Seleziona assistente" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Nessuno —</SelectItem>
                {team.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name} · {t.role}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Duration + start time */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-uppercase text-[11px]">Durata (min)</Label>
              <NumberField
                value={draft.duration}
                onChange={(n) => update({ duration: n })}
                allowDecimals={false}
                min={0}
                className="mt-1 rounded-sm h-11"
                data-testid="timeline-editor-duration"
              />
            </div>
            <div>
              <Label className="label-uppercase text-[11px]">Orario inizio</Label>
              {IS_TOUCH_DEVICE ? (
                // Native iOS/Android time picker — minute-accurate, no
                // 15-min slot list, dismisses the keyboard automatically
                // because a `type=time` field never opens the software
                // keyboard on mobile browsers.
                <input
                  type="time"
                  value={draft.time || ""}
                  onPointerDown={blurActiveTextInput}
                  onChange={(e) => update({ time: e.target.value })}
                  className="mt-1 w-full rounded-sm border border-input bg-white h-11 px-3 text-sm tabular-nums focus:outline-none focus:ring-1 focus:ring-primary"
                  data-testid="timeline-editor-time"
                />
              ) : (
                <TimePicker
                  value={draft.time}
                  onChange={(v) => update({ time: v })}
                  testid="timeline-editor-time"
                  placeholder="Non pianificato"
                />
              )}
              {!draft.time && (
                <p className="text-[10px] text-neutral-500 mt-1">
                  Lascia vuoto per aggiungere a “Da programmare”.
                </p>
              )}
              {draft.time && (parseInt(draft.duration, 10) || 0) > 0 && (
                <p className="text-[10px] text-neutral-500 mt-1 tabular-nums">
                  Fine <span className="text-neutral-800 font-medium">{addMinutes(draft.time, draft.duration)}</span>
                </p>
              )}
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label className="label-uppercase text-[11px]">Note</Label>
            <Textarea
              rows={3}
              value={draft.notes || ""}
              onChange={(e) => update({ notes: e.target.value })}
              onFocus={scrollFocusedIntoView}
              className="mt-1 rounded-sm resize-none"
              placeholder="Aggiungi una nota (facoltativa)…"
              maxLength={500}
              data-testid="timeline-editor-notes"
            />
          </div>

          {mode === "edit" && (
            <div className="pt-2">
              <button
                type="button"
                onPointerDown={blurActiveTextInput}
                onClick={requestDelete}
                className="inline-flex items-center gap-1.5 text-xs text-destructive hover:text-destructive/80"
                data-testid="timeline-editor-delete"
              >
                <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} />
                Elimina questa attività
              </button>
            </div>
          )}

          {/* Spacer so the last field never hides under the sticky footer */}
          <div aria-hidden className="h-2" />
        </form>

        <div
          className="shrink-0 border-t border-border bg-white px-5 py-3 grid grid-cols-2 gap-3"
          style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
        >
          <Button
            type="button"
            variant="outline"
            className="rounded-sm h-11"
            onPointerDown={blurActiveTextInput}
            onClick={requestCancel}
            data-testid="timeline-editor-cancel"
          >
            Annulla
          </Button>
          <Button
            type="button"
            className="rounded-sm h-11 bg-primary hover:bg-primary/90"
            onPointerDown={blurActiveTextInput}
            onClick={commit}
            data-testid="timeline-editor-submit"
          >
            {mode === "new" ? "Aggiungi" : "Salva"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// -------------------------------------------------------------
// Main
// -------------------------------------------------------------
export default function TimelineTab({ client, onClientChange, onSavedTimeline }) {
  const [team, setTeam] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareToken, setShareToken] = useState("");
  const [sharing, setSharing] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [tab, setTab] = useState("scheduled");

  // Editor state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState("new"); // new | edit
  const [draft, setDraft] = useState(null);

  const blocks = useMemo(() => client?.timeline || [], [client?.timeline]);
  const shareUrl = shareToken ? `${window.location.origin}/timeline/share/${shareToken}` : "";
  const canWebShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

  useEffect(() => {
    api.get("/team").then((r) => setTeam(r.data)).catch(() => {});
  }, []);

  const teamLabel = (id) => {
    if (!id) return "—";
    const m = team.find((t) => t.id === id);
    return m ? `${m.name}` : "—";
  };

  const commitBlocks = (next) => {
    onClientChange({ ...client, timeline: next });
  };

  // Split scheduled/unscheduled + conflict detection
  const { scheduled, unscheduled, conflicts, conflictMembers } = useMemo(() => {
    const scheduledArr = blocks
      .filter((b) => toMin(b.time) != null)
      .sort((a, b) => (a.time || "").localeCompare(b.time || ""));
    const unscheduledArr = blocks.filter((b) => toMin(b.time) == null);
    const cf = detectOverlaps(scheduledArr);
    const members = new Set();
    cf.forEach((roles, blockId) => {
      const block = scheduledArr.find((b) => b.id === blockId);
      if (!block) return;
      if (roles.lead.length && block.lead_id) members.add(block.lead_id);
      if (roles.assistant.length && block.assistant_id) members.add(block.assistant_id);
    });
    return {
      scheduled: scheduledArr,
      unscheduled: unscheduledArr,
      conflicts: cf,
      conflictMembers: members,
    };
  }, [blocks]);

  // -------------------- Editor handlers --------------------
  const openNew = () => {
    setEditorMode("new");
    setDraft({
      ...EMPTY_DRAFT,
      id: newBlockId(),
      time: client.work_start_time || "",
      duration: 60,
    });
    setEditorOpen(true);
  };

  const openEdit = (block) => {
    setEditorMode("edit");
    setDraft({ ...EMPTY_DRAFT, ...block });
    setEditorOpen(true);
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setDraft(null);
  };

  const submitDraft = (finalBlock) => {
    let next;
    if (editorMode === "new") {
      next = [...blocks, finalBlock];
      toast.success(finalBlock.time ? "Attività aggiunta" : "Aggiunta a “Da programmare”");
    } else {
      next = blocks.map((b) => (b.id === finalBlock.id ? finalBlock : b));
      toast.success("Attività aggiornata");
    }
    commitBlocks(next);
    // Auto-switch tab to reflect where the block landed
    setTab(finalBlock.time ? "scheduled" : "unscheduled");
    closeEditor();
  };

  const deleteFromEditor = () => {
    if (!draft) return;
    commitBlocks(blocks.filter((b) => b.id !== draft.id));
    toast.success("Attività eliminata");
    closeEditor();
  };

  // -------------------- Share/PDF/Email (unchanged) --------------------
  const ensureShareToken = async () => {
    if (shareToken) return shareToken;
    setSharing(true);
    try {
      const { data } = await api.post(`/clients/${client.id}/timeline/share`);
      setShareToken(data.token);
      return data.token;
    } finally { setSharing(false); }
  };

  const openShareDialog = async () => {
    setShareOpen(true);
    if (!shareToken) {
      try { await ensureShareToken(); }
      catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    }
  };

  const downloadTimelinePdf = async () => {
    try {
      const res = await api.get(`/clients/${client.id}/timeline/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const filename = `timeline-${(client.name || "cliente").replace(/[\s/\\]+/g, "_")}.pdf`;
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      try { window.open(url, "_blank"); } catch {}
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      toast.success("Timeline PDF scaricata");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message || "Errore PDF");
    }
  };

  const copyShareLink = async () => {
    try {
      const t = await ensureShareToken();
      const url = `${window.location.origin}/timeline/share/${t}`;
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const input = document.createElement("input");
        input.value = url; document.body.appendChild(input);
        input.select(); document.execCommand("copy"); input.remove();
      }
      toast.success("Link copiato negli appunti");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message || "Impossibile copiare");
    }
  };

  const sendEmail = async () => {
    if (!client?.email) {
      toast.error("Aggiungi l'email del cliente prima di poter inviare la timeline");
      return;
    }
    setEmailing(true);
    try {
      const { data } = await api.post(`/clients/${client.id}/timeline/email`, {
        app_origin: window.location.origin,
      });
      toast.success(`Email inviata a ${data?.to || client.email}`);
      setShareOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message || "Errore invio email");
    } finally { setEmailing(false); }
  };

  const webShare = async () => {
    try {
      const t = await ensureShareToken();
      const url = `${window.location.origin}/timeline/share/${t}`;
      const title = `Timeline · ${client.name || "Matrimonio"}`;
      const text = "Ecco la timeline della giornata.";
      if (canWebShare) await navigator.share({ title, text, url });
      else await copyShareLink();
    } catch (err) {
      if (err?.name !== "AbortError")
        toast.error(formatApiError(err.response?.data?.detail) || err.message || "Condivisione non disponibile");
    }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/clients/${client.id}/timeline/generate`);
      onSavedTimeline(data.timeline);
      toast.success("Timeline generata");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setGenerating(false); }
  };

  const totalConflicts = conflicts.size;
  const scheduledCount = scheduled.length;
  const unscheduledCount = unscheduled.length;
  const activeList = tab === "scheduled" ? scheduled : unscheduled;

  return (
    <div className="bg-white border border-border rounded-sm p-4 sm:p-6 space-y-4" data-testid="timeline-tab">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h2 className="font-serif-heading text-xl sm:text-2xl">Timeline giornata</h2>
          <p className="text-xs sm:text-sm text-neutral-500 mt-1">
            Sequenza oraria dei servizi fino alla cerimonia. Tocca una card per modificare.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="rounded-sm border-primary text-primary hover:bg-primary hover:text-primary-foreground h-9"
                data-testid="timeline-generate-button"
              >
                <Wand2 className="w-4 h-4 mr-1.5" strokeWidth={1.5} />
                <span className="hidden xs:inline">{blocks.length ? "Rigenera" : "Genera"}</span>
                <span className="xs:hidden">{blocks.length ? "Rigenera" : "Genera"}</span>
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="rounded-sm">
              <AlertDialogHeader>
                <AlertDialogTitle>Generare la timeline?</AlertDialogTitle>
                <AlertDialogDescription>
                  La timeline esistente verrà sostituita con una nuova bozza calcolata su:
                  inizio lavoro <b>{client.work_start_time || "—"}</b>, cerimonia <b>{client.ceremony_time || "—"}</b>,
                  persone da preparare <b>{client.people_count ?? 1}</b>.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="rounded-sm">Annulla</AlertDialogCancel>
                <AlertDialogAction
                  onClick={generate}
                  disabled={generating}
                  className="rounded-sm bg-primary hover:bg-primary/90"
                  data-testid="timeline-generate-confirm"
                >
                  {generating ? "Generazione…" : "Genera"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <Button
            size="sm"
            className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground h-9"
            onClick={openShareDialog}
            data-testid="timeline-share-button"
          >
            <Share2 className="w-4 h-4 mr-1.5" strokeWidth={1.5} />
            Condividi
          </Button>
        </div>
      </div>

      {/* Missing wedding info hint */}
      {(!client.work_start_time || !client.ceremony_time) && (
        <div className="border border-dashed border-border rounded-sm p-3 text-xs sm:text-sm text-neutral-600 bg-secondary/30">
          Per generare la timeline imposta <b>orario inizio lavoro</b> e <b>orario cerimonia</b> nel tab Wedding.
        </div>
      )}

      {/* Conflicts banner */}
      {totalConflicts > 0 && (
        <div
          className="border border-red-300 bg-red-50 rounded-sm p-3 flex items-start gap-2"
          data-testid="timeline-conflicts-banner"
        >
          <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
          <div className="text-xs sm:text-sm text-red-700">
            <div className="font-medium">
              {totalConflicts === 1
                ? "Rilevato 1 blocco con sovrapposizione"
                : `Rilevati ${totalConflicts} blocchi con sovrapposizioni`}
            </div>
            <div className="text-[11px] sm:text-xs mt-0.5">
              Stesso operatore su più blocchi
              {conflictMembers.size > 0 && (
                <> ({Array.from(conflictMembers).map((id) => teamLabel(id)).join(", ")})</>
              )}
              . Modifica orari o riassegna per risolvere.
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-border" role="tablist">
        <button
          role="tab"
          aria-selected={tab === "scheduled"}
          onClick={() => setTab("scheduled")}
          data-testid="timeline-tab-scheduled"
          className={[
            "relative px-3 sm:px-4 py-2 text-sm font-medium transition -mb-px border-b-2",
            tab === "scheduled"
              ? "text-primary border-primary"
              : "text-neutral-500 border-transparent hover:text-neutral-800",
          ].join(" ")}
        >
          Programmati <span className="text-neutral-400 tabular-nums">({scheduledCount})</span>
        </button>
        <button
          role="tab"
          aria-selected={tab === "unscheduled"}
          onClick={() => setTab("unscheduled")}
          data-testid="timeline-tab-unscheduled"
          className={[
            "relative px-3 sm:px-4 py-2 text-sm font-medium transition -mb-px border-b-2",
            tab === "unscheduled"
              ? "text-primary border-primary"
              : "text-neutral-500 border-transparent hover:text-neutral-800",
          ].join(" ")}
        >
          Da programmare <span className="text-neutral-400 tabular-nums">({unscheduledCount})</span>
        </button>
      </div>

      {/* List */}
      <div className="relative">
        {activeList.length === 0 ? (
          <div className="border border-dashed border-border rounded-sm p-8 text-center" data-testid="timeline-empty">
            <Clock className="w-8 h-8 mx-auto text-primary" strokeWidth={1.5} />
            <h3 className="font-serif-heading text-lg mt-3">
              {tab === "scheduled" ? "Nessuna attività programmata" : "Nessuna attività da programmare"}
            </h3>
            <p className="text-xs sm:text-sm text-neutral-600 mt-1.5 max-w-sm mx-auto">
              {tab === "scheduled"
                ? "Aggiungi la prima attività con “+ Nuovo” o genera una bozza automatica."
                : "Le attività senza orario compaiono qui: aggiungine una con “+ Nuovo”."}
            </p>
          </div>
        ) : (
          <ol className="space-y-2.5 pb-24 sm:pb-4" data-testid="timeline-list">
            {activeList.map((b, idx) => (
              <li key={b.id} data-testid={`timeline-block-${idx}`}>
                <TimelineCard
                  block={b}
                  team={team}
                  hasConflict={conflicts.has(b.id)}
                  onOpen={() => openEdit(b)}
                  testid={`timeline-card-${idx}`}
                />
              </li>
            ))}
          </ol>
        )}

        {/* Floating "+ Nuovo" button (mobile + desktop) */}
        <div
          className="sticky bottom-3 z-20 flex justify-end pr-1 pointer-events-none sm:static sm:pr-0 sm:mt-2 sm:justify-end"
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        >
          <Button
            type="button"
            onClick={openNew}
            data-testid="timeline-add-block"
            className="pointer-events-auto rounded-full h-12 px-5 shadow-lg bg-primary hover:bg-primary/90 text-primary-foreground sm:rounded-sm sm:h-10 sm:shadow-none"
          >
            <Plus className="w-5 h-5 mr-1.5" strokeWidth={1.75} />
            Nuovo
          </Button>
        </div>
      </div>

      <p className="text-[11px] text-neutral-500 pt-3 border-t border-border">
        Salva le modifiche con il bottone “Salva” in alto a destra per persistere la timeline.
      </p>

      {/* Editor */}
      <BlockEditor
        open={editorOpen}
        mode={editorMode}
        draft={draft}
        setDraft={setDraft}
        team={team}
        onCancel={closeEditor}
        onSubmit={submitDraft}
        onDelete={deleteFromEditor}
        defaultStartTime={client.work_start_time}
      />

      {/* Share dialog (unchanged) */}
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="share-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-heading text-2xl">Condividi timeline</DialogTitle>
            <DialogDescription>
              Scegli come inviare il programma della giornata a sposa, team o wedding planner.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-2">
            <Label className="label-uppercase">Link condivisibile (sola lettura)</Label>
            <div className="mt-1 flex items-center gap-2">
              <Input
                readOnly
                value={sharing ? "Generazione link…" : (shareUrl || "—")}
                className="rounded-sm text-xs font-mono bg-secondary/30"
                onFocus={(e) => e.target.select()}
                data-testid="share-link-input"
              />
            </div>
            <p className="text-[11px] text-neutral-500 mt-1.5">
              Chiunque abbia il link potrà visualizzare la timeline. Non sarà possibile modificarla.
            </p>
          </div>

          <div className="grid gap-2 mt-4">
            <Button variant="outline" className="rounded-sm justify-start h-auto py-3" onClick={downloadTimelinePdf} data-testid="share-pdf-button">
              <Download className="w-4 h-4 mr-3 text-primary" strokeWidth={1.5} />
              <div className="text-left">
                <div className="font-medium">Esporta PDF</div>
                <div className="text-xs text-neutral-500">Scarica il programma stampabile</div>
              </div>
            </Button>

            <Button variant="outline" className="rounded-sm justify-start h-auto py-3" onClick={sendEmail} disabled={emailing || sharing} data-testid="share-email-button">
              <Mail className="w-4 h-4 mr-3 text-primary" strokeWidth={1.5} />
              <div className="text-left">
                <div className="font-medium">{emailing ? "Invio in corso…" : "Invia via email"}</div>
                <div className="text-xs text-neutral-500">
                  {client?.email ? `Email professionale a ${client.email}` : "Aggiungi l'email del cliente per abilitare"}
                </div>
              </div>
            </Button>

            <Button variant="outline" className="rounded-sm justify-start h-auto py-3" onClick={copyShareLink} disabled={sharing} data-testid="share-copy-button">
              <Copy className="w-4 h-4 mr-3 text-primary" strokeWidth={1.5} />
              <div className="text-left">
                <div className="font-medium">Copia link condivisibile</div>
                <div className="text-xs text-neutral-500">Incollalo dove preferisci</div>
              </div>
            </Button>

            <Button variant="outline" className="rounded-sm justify-start h-auto py-3" onClick={webShare} disabled={sharing} data-testid="share-native-button">
              <Smartphone className="w-4 h-4 mr-3 text-primary" strokeWidth={1.5} />
              <div className="text-left">
                <div className="font-medium">Condividi…</div>
                <div className="text-xs text-neutral-500">
                  {canWebShare ? "Apri il menu nativo (WhatsApp, Mail, AirDrop…)" : "Copia il link (menu nativo non disponibile)"}
                </div>
              </div>
            </Button>
          </div>

          {shareUrl && (
            <a
              href={shareUrl}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-primary hover:underline mt-2 inline-flex items-center gap-1"
              data-testid="share-preview-link"
            >
              <LinkIcon className="w-3 h-3" strokeWidth={1.5} /> Anteprima pubblica
            </a>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
